import React from 'react';
import {
  Alert,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Checkbox,
} from '@c2fo/liquidity';
import type { SimulatorTemplate } from '../types';

interface TemplateRendererProps {
  templates: SimulatorTemplate[];
  selectedTemplate: SimulatorTemplate | null;
  selectTemplate: (template: SimulatorTemplate | null) => void;
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  availableCurrencies: string[];
  displayOptions: {
    showTable: boolean;
    showOfferColumn: boolean;
    showDiscountPercentColumn: boolean;
    showDiscountDollarColumn: boolean;
    showNet: boolean;
    showVendorId: boolean;
    showSummations: boolean;
    lastParticipationDate: boolean;
  };
  setDisplayOptions: React.Dispatch<
    React.SetStateAction<{
      showTable: boolean;
      showOfferColumn: boolean;
      showDiscountPercentColumn: boolean;
      showDiscountDollarColumn: boolean;
      showNet: boolean;
      showVendorId: boolean;
      showSummations: boolean;
      lastParticipationDate: boolean;
    }>
  >;
  renderedTemplate: { intro: string; outro: string; table: string } | null;
  copiedToClipboard: boolean;
  clipboardError: string | null;
  onCopyToClipboard: () => void;
}

export const TemplateRenderer: React.FC<TemplateRendererProps> = ({
  templates,
  selectedTemplate,
  selectTemplate,
  selectedCurrency,
  setSelectedCurrency,
  availableCurrencies,
  displayOptions,
  setDisplayOptions,
  renderedTemplate,
  copiedToClipboard,
  clipboardError,
  onCopyToClipboard,
}) => {
  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="template-select">Template</Label>
          <Select
            value={selectedTemplate?.uuid ?? ''}
            onValueChange={(value) => {
              const template = templates.find((t) => t.uuid === value);
              selectTemplate(template ?? null);
            }}
          >
            <SelectTrigger id="template-select">
              <SelectValue placeholder="Select template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.uuid} value={template.uuid}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <Label htmlFor="currency-select">Currency</Label>
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger id="currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((currency) => (
                <SelectItem key={currency} value={currency}>
                  {currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center">
            <Checkbox
              id="show-table"
              checked={displayOptions.showTable}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showTable: e.target.checked }))}
            />
            <Label htmlFor="show-table" className="ml-2 font-normal cursor-pointer">
              Show Table
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="show-offer-column"
              checked={displayOptions.showOfferColumn}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showOfferColumn: e.target.checked }))}
            />
            <Label htmlFor="show-offer-column" className="ml-2 font-normal cursor-pointer">
              Show Offer Column
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="show-discount-percent-column"
              checked={displayOptions.showDiscountPercentColumn}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showDiscountPercentColumn: e.target.checked }))}
            />
            <Label htmlFor="show-discount-percent-column" className="ml-2 font-normal cursor-pointer">
              Show Discount % Column
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="last-participation-date"
              checked={displayOptions.lastParticipationDate}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, lastParticipationDate: e.target.checked }))}
            />
            <Label htmlFor="last-participation-date" className="ml-2 font-normal cursor-pointer">
              Last Participation Date
            </Label>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center">
            <Checkbox
              id="show-discount-dollar-column"
              checked={displayOptions.showDiscountDollarColumn}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showDiscountDollarColumn: e.target.checked }))}
            />
            <Label htmlFor="show-discount-dollar-column" className="ml-2 font-normal cursor-pointer">
              Show Discount $ Column
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="show-net"
              checked={displayOptions.showNet}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showNet: e.target.checked }))}
            />
            <Label htmlFor="show-net" className="ml-2 font-normal cursor-pointer">
              Show Net
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="show-vendor-id"
              checked={displayOptions.showVendorId}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showVendorId: e.target.checked }))}
            />
            <Label htmlFor="show-vendor-id" className="ml-2 font-normal cursor-pointer">
              Show VendorId
            </Label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="show-summations"
              checked={displayOptions.showSummations}
              onChange={(e) => setDisplayOptions((prev) => ({ ...prev, showSummations: e.target.checked }))}
            />
            <Label htmlFor="show-summations" className="ml-2 font-normal cursor-pointer">
              Show Summations
            </Label>
          </div>
        </div>
      </div>

      {renderedTemplate ? (
        <div className="mt-2 space-y-2">
          <div
            className="whitespace-pre-wrap text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderedTemplate.intro }}
          />
          {displayOptions.showTable && (
            <div className="overflow-x-auto" dangerouslySetInnerHTML={{ __html: renderedTemplate.table }} />
          )}
          <div
            className="whitespace-pre-wrap text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderedTemplate.outro }}
          />
          <div className="mt-4 space-y-2">
            {clipboardError && <Alert variant="filled" type="error" full description={clipboardError} />}
            <Button variant="secondary" onClick={onCopyToClipboard} disabled={!renderedTemplate}>
              {copiedToClipboard ? 'Copied!' : 'Copy to clipboard'}
            </Button>
          </div>
        </div>
      ) : selectedTemplate ? (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-gray-600">
          No simulation data available. Run a simulation to generate email content.
        </div>
      ) : (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-gray-600">
          No template selected. Please select a template from the dropdown above.
        </div>
      )}
    </div>
  );
};
